import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Queries and commands for `api/stla/user-access`.
 *
 * The list is paginated over distinct users rather than rule rows, so a page is
 * a list of groups and `total*` counts describe the whole filter, not the page.
 * Every command invalidates the list, which is the only cached thing it changes.
 */

export type UserAccessScope = 'Country' | 'Dealer';

export interface UserAccessRule {
  id: number;
  scopeType: UserAccessScope;
  scopeValue: string;
  /** UTC ISO timestamp. */
  createdAt: string;
}

export interface UserAccessGroup {
  userId: string;
  /** Null when the Identity account behind the rule is gone. */
  fullName: string | null;
  email: string | null;
  rules: UserAccessRule[];
}

export interface UserAccessPage {
  page: number;
  pageSize: number;
  totalPages: number;
  totalUsers: number;
  totalRules: number;
  groups: UserAccessGroup[];
}

export interface UserAccessUserOption {
  id: string;
  email: string;
  userName: string;
  fullName: string;
}

export interface UserAccessDealerOption {
  vatCode: string;
  companyName: string;
  countryCode: string | null;
}

export interface UserAccessOptions {
  users: UserAccessUserOption[];
  countryCodes: string[];
}

export interface CreateUserAccessRequest {
  userIdentifier: string;
  scopeType: UserAccessScope;
  countryCode?: string | null;
  vatCode?: string | null;
}

const BASE = '/api/stla/user-access';

const keys = {
  list: (page: number, q: string) => ['stla', 'user-access', 'list', page, q] as const,
  options: ['stla', 'user-access', 'options'] as const,
  countries: ['stla', 'user-access', 'countries'] as const,
  dealers: ['stla', 'user-access', 'dealers'] as const,
};

/** The label shown in the picker; the same shape the Razor `<datalist>` rendered. */
export function userDisplay(user: UserAccessUserOption): string {
  return user.fullName ? `${user.fullName} — ${user.email}` : user.email;
}

export function useUserAccessList(page: number, q: string): UseQueryResult<UserAccessPage> {
  return useQuery({
    queryKey: keys.list(page, q),
    queryFn: () => api.get<UserAccessPage>(BASE, { query: { page, q: q || undefined } }),
  });
}

export function useUserAccessOptions(): UseQueryResult<UserAccessOptions> {
  return useQuery({
    queryKey: keys.options,
    queryFn: () => api.get<UserAccessOptions>(`${BASE}/options`),
    // Accounts and country codes barely move, and the picker is on screen for
    // the whole session.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Just the country codes, for callers that have no use for the account list
 * `options` carries alongside them — the create-user wizard scopes the account it
 * is creating, so the picker of existing users is dead weight there.
 */
export function useUserAccessCountries(enabled = true): UseQueryResult<string[]> {
  return useQuery({
    queryKey: keys.countries,
    queryFn: () => api.get<string[]>(`${BASE}/countries`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Dealer options for the VAT autocomplete. Thousands of rows, so it is only
 * fetched once the Dealer scope is actually selected — pass `enabled: false`
 * until then.
 */
export function useUserAccessDealers(enabled: boolean): UseQueryResult<UserAccessDealerOption[]> {
  return useQuery({
    queryKey: keys.dealers,
    queryFn: () => api.get<UserAccessDealerOption[]>(`${BASE}/dealers`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** What `GET dealers/exists` answers. */
export interface DealerLookup {
  exists: boolean;
  /** The code as stored, null when nothing matched. Save this, not what was typed. */
  vatCode: string | null;
}

/**
 * Resolves one VAT code against the campaign tables.
 *
 * A mutation rather than a query because it runs on a button press, not on render,
 * and each call asks about a different code — there is nothing to cache under a
 * stable key. Used to refuse an unknown dealer at the moment it is added, instead
 * of letting the whole form fail on submit.
 */
export function useDealerLookup() {
  return useMutation({
    mutationFn: (vatCode: string) =>
      api.get<DealerLookup>(`${BASE}/dealers/exists`, { query: { vatCode } }),
  });
}

function useInvalidateList() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['stla', 'user-access', 'list'] });
}

export function useCreateUserAccess() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (request: CreateUserAccessRequest) => api.post<UserAccessRule>(BASE, request),
    onSuccess: invalidate,
  });
}

export function useDeleteUserAccess() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`${BASE}/${id}`),
    onSuccess: invalidate,
  });
}
